/* eslint-disable no-case-declarations */
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import type { Company, Contact, Deal, NoteActivity, Quote, Task, EventActivity } from '@paintscout/api'
import {
  useContactQuery,
  useDealQuery,
  useSaveEventMutation,
  useSearchActivitiesQuery,
  useSearchQuotesQuery,
  useUpdateDealStageMutation
} from '@paintscout/api'
import {
  Alert,
  AlertDialog,
  ConfirmationDialog,
  DetailDocList,
  DetailsContainer,
  DocsContainer,
  EditTagsDialog,
  Grid,
  Hidden,
  MapContainer,
  Paper,
  PipelineSelectContainer,
  Tab,
  Tabs,
  TagContainer,
  useClientOptions,
  useCrmDialogs,
  useDebouncedCallback,
  useMediaQuery,
  useUser,
  useUserPermissions,
  useUsers,
  useIsMobile,
  PipelineProgressBar,
  makeStyles
} from '@paintscout/ui'
import { motion } from 'framer-motion'
import DealDialog from 'src/components/dialogs/DealDialog'
import { useNavContext } from 'src/components/common/Header/SideNav'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import StarIcon from '@material-ui/icons/Star'
import { addAuth0Prefix, removeAuth0Prefix, isEmailValid, getHighestRole, getHighestRoleName } from '@paintscout/util'
import { hasIntegrationInterface } from '@paintscout/util/src/builder'
import { getQuoteOptions } from '@paintscout/util/builder/quote'

import {
  createTasksFromTemplate,
  getQuoteTypeOptions,
  getTaskTemplateOptions,
  mapActivitiesToCalendarEvents,
  convertToEmptyInvoice,
  convertToInvoice,
  formatCurrency,
  formatName,
  formatPhoneNumber,
  getFeature,
  getObjectLabels,
  getPipelines,
  getTags,
  isUserAssigned,
  isUserOwner,
  mapContactToDealContact,
  stringifyObjectValues,
  uuid,
  updateDealOwner,
  getLeadSources
} from '@paintscout/util/builder'
import type { DashboardEstimator } from '@paintscout/util/builder'
import {
  EditItemFilesDialog,
  CollectPayrixPaymentDialog,
  CollectStripePaymentDialog,
  ConfigurePaymentDialog,
  PaymentsDialog,
  SendPaymentRequestDialog,
  TileListDialog,
  ViewPaymentDialog,
  EditIntegrationItemFilesDialog
} from '@paintscout/react-quote'
import type { ConfiguredPaymentRequest } from '@paintscout/react-quote'
import { jsonToLucene } from 'json-to-lucene'
import { useSnackbar } from 'notistack'
import type { CalendarEvent, Payment, PayrixPaymentType, QuoteDocument, QuoteFile, QuoteTag } from 'paintscout'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFlags } from 'shared/components/common/FlagsProvider'
import {
  useContactMutations,
  useDealMutations,
  useNoteMutations,
  useQuoteMutations,
  useTaskMutations
} from 'shared/hooks'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import DealPaymentsTable from 'src/components/common/DealPaymentsTable'
import type { DealPaymentRow } from 'src/components/common/DealPaymentsTable'
import DocumentTitle from 'src/components/common/DocumentTitle'
import {
  getMappedSourceDetail,
  getDealLeadSourceDetail,
  getDealAcquisitionSourceDetail
} from 'src/components/common/sourceDetails'
import EventDocList from 'src/components/common/EventTable/EventDocList'
import NotesDocList from 'src/components/common/NotesDocList'
import FilesDocList from 'src/components/common/QuoteTable/FilesDocList'
import QuoteDocList from 'src/components/common/QuoteTable/QuoteDocList'
import DealTaskDocList from 'src/components/common/TaskTable/DealTaskDocList'
import RedirectHome from 'src/components/common/RedirectHome'
import { CreateQuoteDialog, TaskDialog } from 'src/components/dialogs'
import ContactSearchDialogNew from 'src/components/dialogs/ContactSearchDialog/ContactSearchDialogNew'
import type { CreateInvoiceArgs } from 'src/components/dialogs/InvoicesDialog'
import LinkQuoteDialog from 'src/components/dialogs/LinkQuoteDialog'
import NoteDialog from 'src/components/dialogs/NoteDialog'
import BasePage from 'src/components/layouts/BasePage'
import RecoveryNotice from 'src/components/common/EditableQuote/RecoveryNotice'
import { Sentry } from 'src/util/appSentry'
import { goToCompany, goToContact } from 'src/util/routing'
import striptags from 'striptags'
import classNames from 'classnames'
import moment from 'moment'
import DealWorkOrderCard from 'src/components/common/DealWorkOrderCard'
import { mapQuoteToRow } from 'src/components/common/QuoteTable'
import keyBy from 'lodash/keyBy'
import ProductionEventDoc from 'src/components/common/EventTable/ProductionEventDoc'
import ProductionEventDialog from 'src/components/dialogs/EventDialog/ProductionEventDialog'
import { track } from 'shared/util/analytics'
import { ChatActivityComponent } from '../../dashboard/Dashboard/ChatActivityComponent'
import DashboardActivity from '../../dashboard/Dashboard/DashboardActivity'
import { getDocFiles } from '../../people/EditCompany/EditCompany'
import DealHeader from '../DealHeader/DealHeader'
import { ContactDialog, EventDialog, InvoicesDialog } from '../../../dialogs'
import EditDealShim from './EditDealShim'
import { getDealFromRefetchResult } from './dealRefetchSync'
import QuoteDocCard from './QuoteDocCard'
import JobCostingCard from './JobCostingCard'

const DETAILS_ACTIVITY_TYPE = 'details.activityType'
const DETAILS_DEAL_ID = 'details.deal.id'
const DEAL_DETAILS_TAB = 'deal-details'
const TAB_ESTIMATES = 'tab-estimates'
const CONTACT_DETAILS_TAB = 'contact-details'
const WORK_ORDER_TAB = 'work-order'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    body: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'row',
      marginTop: theme.spacing(2),
      borderRadius: theme.borderRadius.lg,
      width: '100%'
    },
    contentWrapper: {
      position: 'relative',
      width: '100%'
    },
    gridItem: {
      marginBottom: theme.spacing,
      '& > div': {
        marginBottom: theme.spacing(2)
      }
    },
    tabs: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(5)
    },
    preferredStar: {
      color: '#f5a622',
      fontSize: '0.75rem',
      marginBottom: theme.spacing(0.25),
      marginLeft: theme.spacing(0.5)
    },
    xlCard: {
      border: `1px solid ${theme.palette.divider} `,
      borderRadius: 4
    },
    alertWithClose: {
      paddingRight: theme.spacing(6)
    }
  }),
  { name: 'EditDeal' }
)

export interface EditDealProps {
  classes?: StyleClasses<typeof useStyles>
}

function EditDeal(_props: EditDealProps) {
  const classes = useStyles()
  const match = useRouteMatch<{
    id?: string
  }>()
  const {
    permissionToCreateQuote,
    permissionToCreateInvoice,
    permissionToEditAssignedDeals,
    permissionToEditAllDeals,
    permissionToCreateTask,
    permissionToCreateEvent
  } = useUserPermissions()
  const { users } = useUsers()
  const history = useHistory()
  const location = useLocation<{ newlyLinkedQuoteId?: string; skipDirtyPrompt?: boolean }>()
  const isMobile = useIsMobile()
  const isXs = useIsMobile({ xs: true })
  const { options, clientId } = useClientOptions()
  const { user, trialExpired, preferences, isCrew, isSales } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const [saveEvent] = useSaveEventMutation()
  const isTwoColumn = useMediaQuery(1470) && !isMobile
  const pipelines = getPipelines({ options })
  const { saveContact } = useContactMutations()
  const id = decodeURIComponent(match.params.id)
  const objectLabels = getObjectLabels({ options })
  const { unlinkDeal, saveQuote, syncQuote } = useQuoteMutations()
  const { deleteNote, updateNote, createNote } = useNoteMutations()
  const { saveDeal, deleteDeals, archiveDeals, untrashDeals } = useDealMutations()
  const { saveTask } = useTaskMutations()
  const { crm: crmFlag, multiPipeline, productionFlag, canDeactivateSettingsItems } = useFlags()
  const quoteTypeOptions = keyBy(
    getQuoteTypeOptions({ options, deleted: true, canDeactivate: !!canDeactivateSettingsItems }),
    'key'
  )
  const multipleInvoicesFeature = getFeature({ options, path: 'quotes.multipleInvoices' })
  const { openDialog, dismissDialog, dismissAllDialogs, setCrmContext } = useCrmDialogs()
  const { loading, data, error, refetch } = useDealQuery({ variables: { id }, fetchPolicy: 'cache-and-network' })
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const isCanadianPayrix = options.options.integrations?.payrix?.config?.country === 'Canada' && hasPayrix
  const hasPaymentProcessing = hasStripe || hasStripeW1d || hasPayrix
  const achLabel = isCanadianPayrix ? 'EFT' : 'ACH'

  const [deal, setDeal] = useState(data?.deal)
  const dealRef = React.useRef(deal)
  dealRef.current = deal
  const isTrashed = deal?.trashed
  const isOwner = deal ? isUserOwner(deal, user.user_id) : false
  const isAssigned = deal ? isUserAssigned(deal, user.user_id) : false
  const pipeline = pipelines.find((p) => p.key === deal?.pipeline?.value)
  const isProductionDeal = pipeline?.key === 'production-pipeline'

  const { setPageSubtitle, setPageChip } = useNavContext()
  useEffect(() => {
    setPageSubtitle(pipeline?.label?.replace('Pipeline', '').trim())
    if (pipeline?.label === 'Production Pipeline') setPageChip({ variant: 'beta' })

    return () => {
      setPageSubtitle('')
      setPageChip(undefined)
    }
  }, [pipeline?.label])

  const canEdit =
    deal &&
    (isOwner || (isAssigned && permissionToEditAssignedDeals) || permissionToEditAllDeals) &&
    !trialExpired &&
    !isTrashed
  const canDelete =
    deal &&
    (permissionToEditAllDeals || (permissionToEditAssignedDeals && (isOwner || isAssigned))) &&
    !trialExpired &&
    !isTrashed
  const canCreateNewItems = (permissionToCreateTask || permissionToCreateEvent) && !isTrashed
  const canUpdateDealStage = !isCrew && !trialExpired && !isTrashed
  const lastSnackbarTimeRef = React.useRef<number>(0) // Debounce for enqueueSnackbar
  const [updateDealStage] = useUpdateDealStageMutation({
    fetchPolicy: 'no-cache' // to avoid caching the updated deal which would refire the rawData useEffect
  })
  const { data: contactData, loading: contactLoading } = useContactQuery({
    variables: { id: deal?.contact?.id },
    skip: !deal?.contact?.id,
    fetchPolicy: 'cache-and-network'
  })

  const { data: productionEventData, loading: productionEventLoading } = useSearchActivitiesQuery({
    variables: {
      query: jsonToLucene({
        filters: [
          {
            id: 'details.eventType',
            type: 'equal',
            value: 'production'
          },
          {
            id: DETAILS_ACTIVITY_TYPE,
            type: 'equal',
            value: 'event'
          },
          {
            id: DETAILS_DEAL_ID,
            type: 'equal',
            value: id
          },
          {
            id: 'trashed',
            type: 'equal',
            not: true,
            value: true
          },
          {
            id: 'details.canceled',
            type: 'equal',
            not: true,
            value: true
          }
        ]
      }),
      limit: 1000
    },
    fetchPolicy: 'no-cache',
    skip: !id || !isProductionDeal
  })

  const {
    activityTab = 'all',
    eventTab = isMobile ? DEAL_DETAILS_TAB : isTwoColumn ? 'quotes' : 'events',
    viewTab: _viewTab = 'details'
  } = getUrlQuery<{
    activityTab: string
    eventTab: string
    viewTab: 'details' | 'activities'
  }>({ location })

  const [isLoading, setIsLoading] = useState(false)
  const [refreshToggle, setRefreshToggle] = useState(false)
  const [addingTemplate, setAddingTemplate] = useState<{ key: string; label: string } | null>(null)
  const [newlyCreatedTasks, setNewlyCreatedTasks] = useState<Task[] | null>(null)
  const [contact, setContact] = useState(contactData?.contact ?? null)
  const [activityRefreshToggle, setActivityRefreshToggle] = useState(false)
  const debouncedActivityRefresh = useDebouncedCallback(() => {
    console.log('debounced now firing')
    setActivityRefreshToggle((prev) => !prev)
  }, 2000)
  const dealFiles = useMemo(() => getDocFiles(deal ?? data?.deal), [deal, data?.deal])
  const dealTags = useMemo(() => getTags({ options, keys: deal?.tags ?? [] }), [deal?.tags, options])
  const [estimatesTab, setEstimatesTab] = useState<'tab-estimates' | 'tab-invoices'>(TAB_ESTIMATES)
  const { data: relatedQuotesData, refetch: refetchRelatedQuotes } = useSearchQuotesQuery({
    variables: {
      query: jsonToLucene({
        filters: [
          {
            id: 'deal_id',
            type: 'equal',
            value: id
          },
          {
            id: 'trashed',
            type: 'equal',
            not: true,
            value: true
          }
        ]
      })
    },
    fetchPolicy: 'no-cache'
  })
  const newlyLinkedQuoteId = location.state?.newlyLinkedQuoteId
  const retryCountRef = useRef(0)
  useEffect(() => {
    if (
      newlyLinkedQuoteId &&
      relatedQuotesData &&
      !relatedQuotesData.searchQuotes?.quotes?.some((q) => q._id === newlyLinkedQuoteId) &&
      retryCountRef.current < 5
    ) {
      const timer = setTimeout(() => {
        retryCountRef.current += 1
        refetchRelatedQuotes()
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [relatedQuotesData, newlyLinkedQuoteId, refetchRelatedQuotes])

  const [optimisticLinkedQuote, setOptimisticLinkedQuote] = useState<Partial<Quote> | null>(null)
  const optimisticRetryCountRef = useRef(0)
  useEffect(() => {
    if (!optimisticLinkedQuote?._id || !relatedQuotesData) return
    if (relatedQuotesData.searchQuotes?.quotes?.some((q) => q._id === optimisticLinkedQuote._id)) {
      setOptimisticLinkedQuote(null)
      optimisticRetryCountRef.current = 0
      return
    }
    if (optimisticRetryCountRef.current < 5) {
      const timer = setTimeout(() => {
        optimisticRetryCountRef.current += 1
        refetchRelatedQuotes()
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [relatedQuotesData, optimisticLinkedQuote, refetchRelatedQuotes])

  const relatedDocIds = relatedQuotesData?.searchQuotes?.quotes?.map((quote) => quote._id) ?? []
  const activityQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'in',
        not: true,
        value: ['edited', 'message', 'event']
      },

      {
        id: 'details.message.deleted',
        type: 'equal',
        not: true,
        value: true
      },
      {
        id: 'docIds',
        type: 'in',
        value: [id, ...relatedDocIds]
      }
    ]
  })

  const chatQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'equal',
        value: 'message'
      },
      {
        id: 'details.message.deleted',
        type: 'equal',
        not: true,
        value: true
      },
      {
        type: 'or',
        value: [
          {
            id: 'details.quote.dealId',
            type: 'equal' as const,
            value: id
          },
          {
            id: 'docIds',
            type: 'in',
            value: relatedDocIds
          }
        ]
      }
    ]
  })

  const notesQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'equal',
        value: 'note'
      },
      {
        id: 'docIds',
        type: 'in',
        not: false,
        value: [id]
      }
    ]
  })

  const contactDetails = useMemo(() => {
    const contactSourceDetail = getMappedSourceDetail({
      title: 'Source',
      leadSource: contact?.lead_source,
      options
    })

    return [
      (contact?.first_name || contact?.last_name) && {
        title: 'Name',
        value: `${contact?.first_name} ${contact?.last_name}`,
        link: () => goToContact(contact._id)
      },
      contact?.companyDocId && {
        title: 'Company',
        value: contact?.company,
        link: contact?.companyDocId ? () => goToCompany(contact.companyDocId) : null
      },
      contact?.address &&
        stringifyObjectValues({ obj: contact, fields: ['address', 'city', 'province', 'postal'] }).length && {
          title: 'Address',
          value: stringifyObjectValues({ obj: deal.jobAddress, fields: ['address', 'city', 'province', 'postal'] }),
          canCopy: true,
          link: () => {
            // Handle address click
            openMap(
              stringifyObjectValues({ obj: contact, fields: ['address', 'city', 'province', 'postal', 'country'] })
            )
          }
        },
      contact?.email && {
        title: 'Email',
        value: contact?.email,
        canCopy: true,
        link: () => window.open(`mailto:${contact?.email}`, '_blank')
      },
      contact?.alternate_email && {
        title: 'Alternate Email',
        value: contact?.alternate_email,
        canCopy: true,
        link: () => window.open(`mailto:${contact?.alternate_email}`, '_blank')
      },
      contact?.phone_number && {
        title: 'Phone',
        value: formatPhoneNumber({ options, phoneNumber: contact?.phone_number, extension: contact?.phone_ext }),
        canCopy: true,
        link: () => {
          const phoneNumber = formatPhoneNumber({
            options,
            phoneNumber: contact?.phone_number,
            extension: contact?.phone_ext
          })
          const formattedNumber = `tel:${phoneNumber}`
          window.open(formattedNumber, '_blank')
        }
      },
      contact?.alternate_phone && {
        title: 'Alternate Phone',
        value: formatPhoneNumber({
          options,
          phoneNumber: contact?.alternate_phone,
          extension: contact?.alternate_phone_ext
        }),
        canCopy: true,
        link: () => {
          const phoneNumber = formatPhoneNumber({
            options,
            phoneNumber: contact?.alternate_phone,
            extension: contact?.alternate_phone_ext
          })
          const formattedNumber = `tel:${phoneNumber}`
          window.open(formattedNumber, '_blank')
        }
      },
      contact?.preferred_contact_method &&
        contact?.preferred_contact_method !== 'any' && {
          title: 'Preferred Contact Method',
          icon: <StarIcon className={classes.preferredStar} />,
          value: (() => {
            switch (contact?.preferred_contact_method) {
              case 'phone':
                return 'Phone'
              case 'email':
                return 'Email'
              case 'sms':
                return 'Text'
              default:
                return ''
            }
          })()
        },
      contactSourceDetail
    ].filter(Boolean)
  }, [contact, contact?.email, contact?.companyDocId, options])

  const [productionEvent, setProductionEvent] = useState<CalendarEvent>(null)
  const [viewTab, setViewTab] = useState<'details' | 'activities'>(_viewTab)
  const [nothingAttached, setNothingAttached] = useState(false)
  const [relatedQuotes, setRelatedQuotes] = useState([])
  const [relatedInvoices, setRelatedInvoices] = useState([])
  const [mobileOnlyActiveTab, setMobileOnlyActiveTab] = useState<string | null>(null)

  const canCreateQuotes = permissionToCreateQuote && relatedQuotes.length === 0
  const canAddQuotes = relatedQuotes.length === 0 && !isCrew && !isSales

  const effectiveEventTab = mobileOnlyActiveTab ?? eventTab
  const eventsTab = getEventsTabs({
    isMobile,
    canCreateQuotes: canCreateQuotes || canAddQuotes,
    productionEvent,
    activeTabValue: effectiveEventTab
  })

  const activitiesTab = [
    {
      label: 'All Activity',
      value: 'all',
      active: activityTab === 'all' || (activityTab === 'tasks' && isMobile),
      allowNew: false
    },
    { label: 'Chat History', value: 'chat', active: activityTab === 'chat', allowNew: false },
    { label: 'Notes', value: 'notes', active: activityTab === 'notes', allowNew: false },
    { label: 'Files', value: 'files', active: activityTab === 'files', allowNew: false },
    isMobile ? null : { label: 'Tasks', value: 'tasks', active: activityTab === 'tasks', allowNew: true }
  ].filter(Boolean)
  const { allowPaymentsOnInvoices, allowACHPaymentsOnInvoices } = getQuoteOptions({
    quote: relatedInvoices[0],
    options
  })
  const dealSources = useMemo(
    () =>
      getLeadSources({ options, type: 'leadSources' }).map((source) => ({
        value: source.name,
        label: source.label
      })),
    [options]
  )

  const handleLeadSourceSelect = useCallback(
    async (option: { label: string; value: string } | null) => {
      const updatedDeal = {
        ...deal,
        lead_source: option?.value ?? null
      }
      setDeal(updatedDeal)
      await saveDeal({ deal: updatedDeal })
    },
    [deal, saveDeal]
  )

  const ownerOptions = useMemo(
    () =>
      (users ?? [])
        .filter((u) => u.app_metadata?.active !== false)
        .map((u) => ({
          value: removeAuth0Prefix(u.user_id),
          label: u.name ?? u.email ?? '',
          color: u.user_metadata?.color,
          role: getHighestRoleName([getHighestRole(u.app_metadata?.roles ?? [])])
        })),
    [users]
  )

  const handleOwnerSelect = useCallback(
    async (option: { label: string; value: string } | null) => {
      if (!option || isTrashed) return
      const newDeal = updateDealOwner({
        deal,
        newOwner: { value: addAuth0Prefix(option.value), label: option.label }
      })
      setDeal(newDeal)
      await saveDeal({ deal: newDeal })
      debouncedActivityRefresh()
    },
    [deal, saveDeal, isTrashed, debouncedActivityRefresh]
  )

  const details = useMemo(() => {
    const leadSourceDetail = getDealLeadSourceDetail({ deal, options })
    const acquisitionSourceDetail = getDealAcquisitionSourceDetail({ deal, options })

    const leadSourceWithSelect =
      isXs && dealSources.length > 0
        ? {
            title: 'Lead Source',
            value: leadSourceDetail?.value ?? 'Select a source',
            selectedValue: deal?.lead_source ?? '',
            selectOptions: dealSources,
            onSelect: handleLeadSourceSelect
          }
        : leadSourceDetail

    const ownerName = deal?.owner
      ? stringifyObjectValues({ obj: deal?.owner, fields: ['firstName', 'lastName'], joiner: ' ' })
      : ''

    return [
      // multiPipeline &&
      // deal?.pipeline &&
      // pipeline && {
      //   title: 'Pipeline',
      //   value: pipeline?.label
      // },
      // deal?.stage &&
      // pipeline && {
      //   title: 'Stage',
      //   value: pipeline?.stages?.find((s) => s.key === deal?.stage?.value)?.label ?? ''
      // },
      deal?.value && {
        title: 'Value',
        value: formatCurrency({ options, value: deal?.value })
      },
      isMobile &&
        (isXs && ownerOptions.length > 0
          ? {
              title: 'Owner',
              value: ownerName || 'Select an owner',
              selectedValue: deal?.owner?.id ?? '',
              selectOptions: ownerOptions,
              onSelect: handleOwnerSelect
            }
          : ownerName.length && {
              title: 'Owner',
              value: ownerName
            }),
      leadSourceWithSelect,
      pipeline?.key === 'sales-pipeline' && deal?.acquisitionSource ? acquisitionSourceDetail : null,
      deal?.created && {
        title: 'Date Created',
        value: moment(deal.created).format('YYYY-MM-DD')
      }
    ].filter(Boolean)
  }, [
    deal,
    deal?._rev,
    deal?.jobAddress,
    deal?.owner,
    deal?.value,
    pipeline,
    relatedInvoices,
    relatedQuotes,
    multiPipeline,
    options,
    isXs,
    dealSources,
    handleLeadSourceSelect,
    ownerOptions,
    handleOwnerSelect
  ])

  const activeEventTab = eventsTab.find((tab) => tab.active)

  // Combine warnings for mobile/tablet
  const warnings = []
  if (deal?.archived) {
    warnings.push('This deal is archived.')
  }
  const combinedWarning = warnings.join(' ')

  useEffect(() => {
    const dataDeal = data?.deal ?? null
    if (dataDeal) {
      if (!deal || deal._rev !== dataDeal._rev) {
        setDeal(dataDeal)
      } else {
        // Even with the same _rev, update if local state is missing FullDeal-only fields.
        // This handles the case where deal was initialized from a SearchedDeal cache entry
        // (e.g. populated by the DealList query) before the full deal query resolved.
        const serverFileCount = Object.keys(dataDeal.files ?? {}).length
        const localFileCount = Object.keys(deal.files ?? {}).length
        const isMissingFullDealFields =
          serverFileCount !== localFileCount ||
          dataDeal.status !== deal.status ||
          JSON.stringify(dataDeal.taskTemplateOrder) !== JSON.stringify(deal.taskTemplateOrder) ||
          dataDeal.trashed !== deal.trashed ||
          dataDeal.deleteDate !== deal.deleteDate
        if (isMissingFullDealFields) {
          setDeal(dataDeal)
        }
      }
      if (!dataDeal?.contact?.id) {
        const now = Date.now()
        if (now - lastSnackbarTimeRef.current > 5000) {
          enqueueSnackbar('Primary Contact has been deleted.', { variant: 'warning' })
          lastSnackbarTimeRef.current = now
        }
      }
      setCrmContext({
        subtitle: dataDeal?.name ? `Deal - ${dataDeal.name}` : '',
        deal: {
          id: dataDeal._id,
          name: dataDeal.name,
          hide: true
        }
      })
    }
  }, [data])

  useEffect(() => {
    if (relatedQuotesData && !nothingAttached) {
      const allQuotes = relatedQuotesData?.searchQuotes?.quotes ?? []
      let newRelatedQuotes = allQuotes.filter((q) => !q.is_invoice)
      const newRelatedInvoices = allQuotes.filter((q) => q.is_invoice)
      // If the optimistic quote hasn't been indexed by ES yet, keep it visible
      if (optimisticLinkedQuote?._id && !newRelatedQuotes.some((q) => q._id === optimisticLinkedQuote._id)) {
        newRelatedQuotes = [optimisticLinkedQuote as (typeof newRelatedQuotes)[0], ...newRelatedQuotes]
      }
      setRelatedQuotes(newRelatedQuotes)
      setRelatedInvoices((prevInvoices) => {
        return newRelatedInvoices.map((newInvoice) => {
          const prevInvoice = prevInvoices.find((inv) => inv._id === newInvoice._id)
          if (prevInvoice) {
            const prevRevSeq = parseInt(prevInvoice._rev?.split('-')[0] || '0', 10)
            const newRevSeq = parseInt(newInvoice._rev?.split('-')[0] || '0', 10)
            // When keeping prevInvoice (higher rev from a recent save), it may lack totalHours
            // since that's a search-computed field not stored in the raw CouchDB document.
            // Merge totalHours from the search result so it's not lost.
            return prevRevSeq > newRevSeq
              ? { ...prevInvoice, totalHours: prevInvoice.totalHours ?? newInvoice.totalHours }
              : newInvoice
          }
          return newInvoice
        })
      })
    }
  }, [relatedQuotesData, optimisticLinkedQuote])

  useEffect(() => {
    if (productionEventData?.searchActivities?.activities?.length > 0) {
      const activity = productionEventData.searchActivities.activities[0] as EventActivity
      setProductionEvent(mapActivitiesToCalendarEvents([activity])[0])
    }
  }, [productionEventData])

  useEffect(() => {
    return () => {
      setCrmContext({})
    }
  }, [])

  useEffect(() => {
    setContact(contactData?.contact)
  }, [contactLoading, contactData])

  function getEventsTabs({
    isMobile,
    canCreateQuotes,
    productionEvent,
    activeTabValue
  }: {
    isMobile: boolean
    canCreateQuotes: boolean
    productionEvent: CalendarEvent | null
    activeTabValue: string
  }) {
    return [
      isMobile
        ? { label: 'Details', value: DEAL_DETAILS_TAB, active: activeTabValue === DEAL_DETAILS_TAB, allowNew: false }
        : null,
      isMobile
        ? {
            label: 'Contact Details',
            value: CONTACT_DETAILS_TAB,
            active: activeTabValue === CONTACT_DETAILS_TAB,
            allowNew: false
          }
        : null,
      (isMobile || !isProductionDeal) && {
        label: 'Events',
        value: isProductionDeal ? 'productionEvent' : 'events',
        active: activeTabValue === 'events' || activeTabValue === 'productionEvent',
        allowNew: permissionToCreateEvent && !productionEvent
      },
      isMobile && productionFlag && isProductionDeal
        ? {
            label: 'Work Order',
            value: WORK_ORDER_TAB,
            active: activeTabValue === WORK_ORDER_TAB,
            allowNew: false
          }
        : null,
      {
        label: objectLabels.quote.value,
        value: 'quotes',
        active: activeTabValue === 'quotes',
        allowNew: canCreateQuotes
      },
      {
        label: 'Invoice',
        value: 'invoices',
        active: activeTabValue === 'invoices',
        allowNew: relatedQuotes.length > 0 && permissionToCreateInvoice && relatedInvoices.length === 0
      },
      {
        label: 'Payments',
        value: 'payments',
        active: activeTabValue === 'payments',
        allowNew: !!(relatedInvoices[0] && relatedInvoices[0].totals.balance_due > 0)
      },
      isProductionDeal && {
        label: 'Job Costing',
        value: 'job-costing',
        active: activeTabValue === 'job-costing',
        allowNew: false
      },
      isMobile ? { label: 'Tasks', value: 'tasks', active: activeTabValue === 'tasks', allowNew: false } : null
    ].filter(Boolean)
  }

  const getQuoteListQuery = () =>
    jsonToLucene({
      filters: [
        { id: 'deal_id', type: 'equal', value: id },
        { id: 'is_invoice', type: 'equal', value: false },
        {
          id: 'trashed',
          type: 'equal',
          not: true,
          value: true
        }
      ]
    })

  const getInvoiceListQuery = () =>
    jsonToLucene({
      filters: [
        { id: 'deal_id', type: 'equal', value: id },
        { id: 'is_invoice', type: 'equal', value: true },
        {
          id: 'trashed',
          type: 'equal',
          not: true,
          value: true
        }
      ]
    })

  function handleEventTabClick(tab: string) {
    if ([DEAL_DETAILS_TAB, CONTACT_DETAILS_TAB].includes(tab)) {
      setMobileOnlyActiveTab(tab)
    } else {
      setMobileOnlyActiveTab(null)
      setUrlQuery({
        location,
        params: {
          eventTab: tab
        }
      })
    }
  }

  function openMap(address) {
    const encodedAddress = encodeURIComponent(address)
    if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
      window.open(`maps://?q=${encodedAddress}`, '_blank')
    } else {
      window.open(`https://www.google.com/maps/search/${encodedAddress}`, '_blank')
    }
  }

  function handleActivityTabClick(tab: string) {
    setUrlQuery({
      location,
      params: {
        activityTab: tab
      }
    })
  }

  function handleSetViewTab(tab: 'details' | 'activities') {
    setUrlQuery({
      location,
      params: {
        viewTab: tab
      }
    })
    setViewTab(tab)
  }

  async function handleRemoveQuoteLink(quoteId: string, skipUnlink: boolean = false) {
    track('deal_unlinked_from_quote', { dealId: deal._id, quoteId })
    if (!skipUnlink) {
      try {
        await unlinkDeal(quoteId)
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            companyId: contact?.companyDocId,
            quoteId,
            error: error.message
          }
        })
        enqueueSnackbar('Unable to unlink deal from quote', { variant: 'error' })
      }
    }
    setRelatedQuotes([])
    setRelatedInvoices([])
    setNothingAttached(true)
    setDeal((prevDeal) => ({
      ...prevDeal,
      updated: Date.now(),
      quoteType: undefined,
      value: undefined,
      hours: undefined
    }))
    const refetchResult = await refetch().catch((error) => {
      Sentry.captureException(error, { tags: { dealId: deal?._id, source: 'handleRemoveQuoteLink.refetch' } })
      return null
    })
    setDeal((prevDeal) => getDealFromRefetchResult(refetchResult, prevDeal) as Deal)
  }

  async function handleFilesChange({
    files,
    replaceExisting = true
  }: {
    files: QuoteFile[]
    replaceExisting?: boolean
  }) {
    const currentDeal = dealRef.current
    const updatedDeal: Deal = {
      ...currentDeal,
      files: {
        ...(replaceExisting
          ? Object.values(currentDeal?.files ?? {}).reduce((acc, file: QuoteFile) => {
              acc[file.key] = {
                ...file,
                deleted: true
              }
              return acc
            }, {})
          : currentDeal?.files),
        ...files.reduce((acc, file) => {
          // if this file key already exists and is delted, use the delted one
          if (currentDeal?.files?.[file.key]?.deleted) {
            acc[file.key] = {
              ...currentDeal?.files[file.key]
            }
          } else {
            acc[file.key] = file
          }

          return acc
        }, {})
      }
    }

    setDeal(updatedDeal)
    const { deal: savedDeal } = await saveDeal({ deal: updatedDeal, delayedRefetchQueries: ['searchActivities'] })
    setDeal(savedDeal)
  }

  async function handleCreateNote(files: QuoteFile[], note: string) {
    track('note_created', { docType: 'deal', docId: deal._id })
    // Upload the files to deal, then create the note activity
    const hasNote = striptags(note).trim().length > 0
    const notedFiles = files.map((file) => ({
      ...file,
      onNote: hasNote
    }))
    if (notedFiles.length > 0) {
      await handleFilesChange({ files: notedFiles, replaceExisting: false })
    }
    if (hasNote) {
      await createNote({
        docId: deal?._id,
        docType: 'deal',
        note,
        files: files.map((file) => file.key)
      })
    }
  }

  async function handleSaveNote(id: string, files: QuoteFile[], updatedFiles: QuoteFile[], note: string) {
    // Upload the files to contact, then create the note activity
    const notedFiles = [...files, ...updatedFiles].map((file) => ({
      ...file,
      onNote: true
    }))
    await handleFilesChange({ files: notedFiles, replaceExisting: false })
    await updateNote({
      id,
      note,
      files: files.map((file) => file.key)
    })
    dismissDialog()
  }

  async function handleDeleteNote(note: NoteActivity) {
    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message: `This note ${note?.details?.files?.length > 0 ? 'and its attached files ' : ''}will be permanently deleted from the deal and cannot be undone.`,
      onConfirm: async () => {
        track('note_deleted', { noteId: note?._id })
        await deleteNote(note?._id)
        if (note?.details?.files?.length > 0) {
          const updatedFiles = dealFiles.filter((file) => !note?.details?.files?.includes(file.key))
          handleFilesChange({ files: updatedFiles, replaceExisting: true })
        }
        dismissAllDialogs()
      },
      onCancel: dismissDialog
    })
  }

  function handleEditNote(note: NoteActivity) {
    openDialog(NoteDialog, {
      noteId: note?._id,
      docId: deal?._id,
      onConfirm: async (noteDoc: Company | Contact | Deal, noteText: string, files: QuoteFile[], isDirty?: boolean) => {
        if (isDirty) {
          const removedFiles = note.details.files.filter((fileKey) => !files.some((file) => file.key === fileKey))
          const updatedFiles = getDocFiles(noteDoc).map((file) => {
            if (removedFiles.includes(file.key)) {
              return { ...file, deleted: true }
            }
            return file
          })
          // update the existing note, and its related doc
          await handleSaveNote(note._id, files, updatedFiles, noteText)
        }
        dismissDialog()
      },
      onCancel: dismissDialog,
      onDelete: (note: NoteActivity) => {
        handleDeleteNote(note)
      }
    })
  }

  function handleNoteFileDetails(fileIdString: string): QuoteFile {
    const fileId = fileIdString.split('_')[1]
    const file = dealFiles.find((file) => file.key === fileId)
    return file
  }

  function handleEditNoteFile(activity: NoteActivity) {
    const noteFiles = Object.values(deal.files).filter((file: QuoteFile) =>
      activity.details.files.includes(file.key)
    ) as QuoteFile[]
    openDialog(EditItemFilesDialog, {
      fileDocId: deal?._id,
      titleOnly: true,
      files: noteFiles as QuoteFile[],
      index: 0,
      viewOnly: !canEdit,
      onConfirm: async (newFiles, deletedFiles) => {
        const filesUpdated =
          newFiles.length !== noteFiles.length ||
          newFiles.some((file) => {
            const existingFile = noteFiles.find((f) => f.key === file.key)
            return existingFile?.title !== file.title || existingFile?.type !== file.type
          })
        if (filesUpdated) {
          const updatedFiles = dealFiles.map((file) => {
            // if file is in deletedFiles, mark it as deleted
            const deletedFile = deletedFiles.find((deletedFile) => deletedFile.key === file.key)
            if (deletedFile) {
              return { ...file, deleted: true }
            } else {
              return file
            }
          })
          console.log('newFiles', { newFiles, filesUpdated })
          await handleSaveNote(activity._id, newFiles, updatedFiles, activity.details.note)
          setTimeout(() => {
            setRefreshToggle((prev) => !prev)
          }, 500)
        }

        dismissDialog()
      },
      onCancel: dismissDialog,
      onDelete: (_key, _files) => {}
    })
  }

  const addEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    await saveEvent({
      variables: {
        event,
        eventTemplate
      },
      refetchQueries: ['searchActivities']
    })
  }

  async function handleInvoices() {
    if (!relatedQuotes[0]) return
    setIsLoading(true)
    try {
      // send a bad rev to always get the latest quote back hack, pass through to skip in create
      const {
        data: { syncQuote: fullQuote }
      } = await syncQuote({ id: relatedQuotes[0]._id, rev: 'x' })
      openDialog(InvoicesDialog, {
        quote: fullQuote,
        onCancel: dismissAllDialogs,
        onCreateInvoice: ({ partialAmount, empty }: CreateInvoiceArgs) =>
          handleCreateInvoice({ multiple: true, partialAmount, empty, quote: fullQuote })
      })
    } catch (error) {
      enqueueSnackbar('Unable to open the invoices dialog', { variant: 'error' })
      Sentry.captureException(error)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleCreateInvoice(args: {
    multiple?: boolean
    partialAmount?: number
    empty?: boolean
    redirect?: boolean
    quote?: QuoteDocument
  }) {
    track('invoice_created', { dealId: deal._id })
    const { partialAmount, empty, quote, redirect = true } = args
    setIsLoading(true)
    const {
      data: { syncQuote: fullQuote }
    } = quote ? { data: { syncQuote: quote } } : await syncQuote({ id: relatedQuotes[0]._id, rev: 'x' }) // x will always get our fresh quote back
    const invoice = empty
      ? convertToEmptyInvoice({ quote: fullQuote, options })
      : convertToInvoice({ quote: fullQuote, options, partialAmount })

    // Awaiting save so we dont show up to invoice page with no rev and get double saved.
    void saveQuote({ quote: invoice })
      .then((_) => {
        setIsLoading(false)
        if (redirect) {
          history.push(`/invoices/edit/${invoice._id}/`)
        }
      })
      .catch(() => {
        setIsLoading(false)
        enqueueSnackbar('An error occurred while creating the invoice', { variant: 'error' })
      })
  }

  const handleNewItemClick = ({ type, key }: { type: string; key?: string }) => {
    if (isTrashed) return
    dismissDialog()
    switch (type) {
      case 'productionEvent':
        // if (!relatedInvoices.length) {
        //   enqueueSnackbar('Please link an invoice before creating a production event.', { variant: 'warning' })
        //   return
        // }
        openDialog(ProductionEventDialog, {
          isNew: true,
          event: {
            title: deal?.name,
            eventType: 'production',
            projectType: deal?.quoteType,
            id: uuid(),
            deal: {
              id,
              source: deal?.lead_source,
              name: deal?.name,
              hours: deal?.hours
            },
            production: true,
            address: deal.jobAddress
              ? stringifyObjectValues({ obj: deal.jobAddress, fields: ['address', 'city', 'province', 'postal'] })
              : null,
            contact: deal?.contact
              ? (deal.contact as CalendarEvent['contact'])
              : contact
                ? {
                    id: contact._id,
                    firstName: contact.first_name,
                    lastName: contact.last_name,
                    phoneNumber: contact.phone_number,
                    email: contact.email,
                    preferredContactMethod: contact.preferred_contact_method,
                    companyId: contact.company
                  }
                : undefined,
            estimator: deal?.owner?.id
              ? {
                  id: addAuth0Prefix(deal.owner.id),
                  name: formatName({ firstName: deal?.owner?.firstName, lastName: deal?.owner?.lastName }),
                  email: deal.owner?.email,
                  color: users.find((u) => u.user_id === addAuth0Prefix(deal?.owner?.id))?.user_metadata?.color
                }
              : null
          },
          onConfirm: async (event: CalendarEvent) => {
            await saveEvent({
              variables: {
                event
              },
              refetchQueries: []
            })
            setProductionEvent((prev) => ({
              ...prev,
              ...event
            }))
            dismissDialog()
          },
          onCancelEvent: (event: CalendarEvent) => {
            handleCancelProductionEvent(event)
          }
        })
        break
      case 'event':
      case 'events':
        openDialog(EventDialog, {
          isNew: true,
          event: {
            id: uuid(),
            deal: {
              id,
              source: deal?.lead_source,
              name: deal?.name
            },
            geocode: deal?.geocode,
            address: deal.jobAddress
              ? stringifyObjectValues({ obj: deal.jobAddress, fields: ['address', 'city', 'province', 'postal'] })
              : null,
            contact: deal?.contact
              ? (deal.contact as CalendarEvent['contact'])
              : contact
                ? {
                    id: contact._id,
                    firstName: contact.first_name,
                    lastName: contact.last_name,
                    phoneNumber: contact.phone_number,
                    email: contact.email,
                    preferredContactMethod: contact.preferred_contact_method,
                    companyId: contact.company
                  }
                : undefined,
            estimator: deal?.owner?.id
              ? {
                  id: addAuth0Prefix(deal.owner.id),
                  name: formatName({ firstName: deal?.owner?.firstName, lastName: deal?.owner?.lastName }),
                  email: deal.owner?.email,
                  color: users.find((u) => u.user_id === addAuth0Prefix(deal?.owner?.id))?.user_metadata?.color
                }
              : null
          },
          onConfirm: (event: CalendarEvent, eventTemplate: string) => {
            if (event.contact && contact && event.contact?.email !== contact?.email) {
              const updatedContact = {
                ...contact,
                email: event.contact?.email
              }
              const updatedDeal = {
                ...deal,
                contact: {
                  ...deal.contact,
                  email: event.contact?.email
                }
              }
              setContact(updatedContact)
              setDeal(updatedDeal)
              saveDeal({ deal: updatedDeal })
              saveContact({
                contact: updatedContact
              })
            }
            addEvent(event, eventTemplate)
            handleSetViewTab('details')
            handleEventTabClick('events')
            debouncedActivityRefresh()
            dismissDialog()
          }
        })
        break
      case 'deal':
      case 'deals':
        openDialog(DealDialog, {
          onConfirm: (_deal: Deal) => {
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'estimate':
      case 'quotes':
        setNothingAttached(false)
        openDialog(LinkQuoteDialog, {
          invoice: false,
          forDeal: deal,
          onConfirm: async (quote: Partial<Quote>) => {
            setRelatedQuotes([quote])
            setOptimisticLinkedQuote(quote)
            optimisticRetryCountRef.current = 0
            await refetchRelatedQuotes()
            const refetchResult = await refetch().catch((error) => {
              Sentry.captureException(error, {
                tags: { dealId: deal?._id, source: 'LinkQuoteDialog.onConfirm.refetch' }
              })
              return null
            })
            setDeal((prevDeal) => getDealFromRefetchResult(refetchResult, prevDeal) as Deal)
            dismissDialog()
          },
          onAddNew: () => {
            openDialog(CreateQuoteDialog, {
              invoice: false,
              forDeal: deal,
              onConfirm: (quote: QuoteDocument) => {
                history.push(`/quotes/new`, { quote })
                dismissAllDialogs()
              },
              onCancel: dismissDialog
            })
          },
          onCancel: () => dismissDialog()
        })

        break
      case 'invoices':
      case 'invoice':
        if (multipleInvoicesFeature) {
          handleInvoices()
        } else {
          handleCreateInvoice({ multiple: false, redirect: true })
        }
        break
      case 'task':
      case 'tasks':
        openDialog(TaskDialog, {
          dealId: deal?._id,
          defaultEstimator: deal?.owner?.id,
          onConfirm: (task: Task) => {
            setNewlyCreatedTasks([task])
            handleSetViewTab('details')
            handleEventTabClick('tasks')
            dismissDialog()
          },
          onCancel: dismissDialog
        })

        break
      case 'taskTemplate':
        handleAddTaskTemplate(key)
        break
      case 'note':
      case 'notes':
        openDialog(NoteDialog, {
          docId: deal?._id,
          onConfirm: async (
            _noteDoc: Company | Contact | Deal,
            note: string,
            files: QuoteFile[],
            _isDirty?: boolean
          ) => {
            await handleCreateNote(files, note)
            handleSetViewTab('activities')
            handleActivityTabClick('notes')
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'files':
      case 'file':
        openDialog(EditItemFilesDialog, {
          fileDocId: deal?._id,
          titleOnly: true,
          files: Object.values(deal?.files ?? {}) as QuoteFile[],
          index: Object.values(deal?.files ?? {}).length,
          onConfirm: async (newFiles, _deletedFiles) => {
            await handleFilesChange({ files: newFiles })
            handleSetViewTab('activities')
            handleActivityTabClick('files')
            debouncedActivityRefresh()
            dismissDialog()
          },
          onCancel: () => {
            dismissDialog()
            // setLoading(false),
          },
          onDelete: (_key, _files) => {}
        })
        break
      case 'file-companycam': {
        const currentFiles = Object.values(deal?.files ?? {}) as QuoteFile[]
        openDialog(EditIntegrationItemFilesDialog, {
          provider: 'companycam',
          maxWidth: 'lg',
          fullWidth: true,
          files: currentFiles,
          onConfirm: (newFiles: QuoteFile[]) => {
            const now = Date.now()
            const existingKeys = new Set(currentFiles.map((f) => f.key))
            const timestampedNewFiles = newFiles
              .filter((f) => !existingKeys.has(f.key))
              .map((f) => ({ ...f, created: f.created ?? now }))
            const mergedFiles = [...currentFiles, ...timestampedNewFiles]
            openDialog(
              EditItemFilesDialog,
              {
                fileDocId: deal?._id,
                files: mergedFiles,
                index: currentFiles.length,
                titleOnly: true,
                onConfirm: async (finalFiles, _deletedFiles) => {
                  await handleFilesChange({ files: finalFiles })
                  handleSetViewTab('activities')
                  handleActivityTabClick('files')
                  debouncedActivityRefresh()
                  dismissDialog()
                },
                onCancel: dismissDialog,
                onDelete: (_key, _files) => {}
              },
              { replace: true }
            )
          },
          onCancel: dismissDialog
        })
        break
      }
      case 'payment':
      case 'payments':
        handleAddPayment()
        break
      default:
        break
    }
  }

  function handleAddPayment() {
    const isPaymentEligible =
      relatedInvoices[0] && relatedInvoices[0].totals.after_tax > 0 && relatedInvoices[0].totals.balance_due > 0
    const canCollectCCPayment = hasPaymentProcessing && allowPaymentsOnInvoices && isPaymentEligible
    const canCollectACHPayment = hasPayrix && allowACHPaymentsOnInvoices && isPaymentEligible
    if (!isPaymentEligible && !canCollectCCPayment && !canCollectACHPayment) {
      handleEditPayment()
      return
    }
    openDialog(TileListDialog, {
      title: 'Add/Request Payment',
      showTopClose: true,
      items: [
        {
          key: 'record-payment',
          label: 'Record Payment'
        },
        isPaymentEligible && {
          key: 'request-payment',
          label: 'Request Payment'
        },
        canCollectCCPayment && {
          key: 'credit',
          label: 'Take Credit Card Payment'
        },
        canCollectACHPayment && {
          key: 'ach',
          label: `Take ${achLabel} Payment`
        }
      ].filter(Boolean),
      onItemSelect: (key: string) => {
        if (key === 'record-payment') {
          handleEditPayment({ backOnCancel: true })
        } else if (key === 'credit' || key === 'ach') {
          handleConfigurePayment(key)
        } else if (key === 'request-payment') {
          handleRequestPayment()
        }
      }
    })
  }

  async function handleRequestPayment() {
    track('quote_payment_requested', { dealId: deal._id })
    if (!relatedInvoices[0]?.contact) {
      openDialog(AlertDialog, {
        title: 'Contact Required',
        message: 'You must add a contact to the invoice before you can request a payment',
        onConfirm: dismissAllDialogs
      })
      return
    } else if (!isEmailValid(relatedInvoices[0].contact.email)) {
      openDialog(AlertDialog, {
        title: 'Invalid Email',
        message: 'You must add a valid email address to your invoice contact before you can request a payment',
        onConfirm: dismissAllDialogs
      })
      return
    }

    openDialog(ConfigurePaymentDialog, {
      quote: relatedInvoices[0],
      preferences,
      isRequest: true,
      buttonLabel: 'Next',
      backOnCancel: true,
      onConfirm: async (args: ConfiguredPaymentRequest) => {
        const payment: Payment = {
          ...args,
          id: uuid(),
          type: null,
          date: Date.now(),
          status: 'pending',
          isRequest: true
        }

        const {
          data: { syncQuote: fullInvoice }
        } = await syncQuote({ id: relatedInvoices[0]._id, rev: 'x' })
        openDialog(SendPaymentRequestDialog, {
          quote: fullInvoice,
          payment,
          user,
          preferences,
          onConfirm: (quote: QuoteDocument) => {
            setRelatedInvoices((prevDocs) => {
              return prevDocs.map((doc) => (doc._id === quote._id ? { totalHours: doc.totalHours, ...quote } : doc))
            })
            debouncedActivityRefresh()
          }
        })
      },
      onCancel: dismissDialog
    })
  }

  function handleConfigurePayment(paymentType: PayrixPaymentType = 'credit') {
    openDialog(ConfigurePaymentDialog, {
      quote: relatedInvoices[0],
      preferences,
      backOnCancel: true,
      hideSurcharge: paymentType === 'ach',
      onCancel: dismissDialog,
      onConfirm: (paymentArgs) => {
        handleCollectPayment({ ...paymentArgs, paymentType })
      }
    })
  }

  function handleCollectPayment({
    amount,
    note,
    deposit,
    paymentRequestId,
    paymentType
  }: {
    amount: number
    note: string
    deposit: boolean
    paymentRequestId?: string
    paymentType?: PayrixPaymentType
  }) {
    track('payment_collected', { dealId: deal._id })
    if (!hasPaymentProcessing) {
      Sentry.captureException(new Error('CollectPaymentDialog tried to open without a valid payment provider'))
      enqueueSnackbar(
        'Something is wrong with your payment integration. Please contact our support team for assistance.',
        { variant: 'error' }
      )
      dismissAllDialogs()
      return
    }
    const PaymentDialog = hasPayrix ? CollectPayrixPaymentDialog : CollectStripePaymentDialog
    openDialog(PaymentDialog, {
      quote: relatedInvoices[0],
      amount,
      note,
      options,
      companyId: clientId,
      isDeposit: deposit,
      source: 'collect-payment',
      paymentRequestId,
      paymentType,
      backOnCancel: true,
      onCancel: dismissDialog,
      onConfirm: async (updatedQuote: QuoteDocument) => {
        const { quote: savedQuote } = await saveQuote({
          quote: {
            ...updatedQuote,
            dates: {
              ...updatedQuote.dates,
              updated: Date.now()
            }
          }
        })
        setRelatedInvoices((prevDocs) => {
          return prevDocs.map((doc) =>
            doc._id === savedQuote._id ? { ...savedQuote, totalHours: savedQuote.totalHours ?? doc.totalHours } : doc
          )
        })
        dismissAllDialogs()
      }
    })
  }

  function handleViewProcessedPayment({
    activityId,
    row,
    quote
  }: {
    activityId: string
    row?: DealPaymentRow
    quote?: QuoteDocument
  }) {
    openDialog(ViewPaymentDialog, {
      quote: quote || (row.quote as QuoteDocument),
      onDeal: true,
      activityId: activityId,
      onClose: dismissDialog
    })
  }

  function handleEditPayment({
    paymentIndex,
    backOnCancel,
    markingPaid
  }: { paymentIndex?: number; backOnCancel?: boolean; markingPaid?: boolean } = {}) {
    if (relatedInvoices[0].payments[paymentIndex]?.activityId && !relatedInvoices[0].payments[paymentIndex]?.manual) {
      handleViewProcessedPayment({
        activityId: relatedInvoices[0].payments[paymentIndex]?.activityId,
        quote: relatedInvoices[0]
      })
    } else {
      openDialog(PaymentsDialog, {
        quote: relatedInvoices[0],
        quoteId: relatedInvoices[0]._id,
        paymentIndex,
        backOnCancel,
        markingPaid,
        onDeal: true,
        onConfirm: (quote) => {
          setRelatedInvoices((prevDocs) => {
            return prevDocs.map((doc) => (doc._id === quote._id ? { totalHours: doc.totalHours, ...quote } : doc))
          })
          debouncedActivityRefresh()
          dismissAllDialogs()
        },
        onCancel: dismissDialog
      })
    }
  }

  const handleNewContact = () => {
    openDialog(ContactSearchDialogNew, {
      blockIntegration: true,
      onCreateNew: (queryString: string) => {
        openDialog(ContactDialog, {
          queryString,
          disableDelete: true,
          confirmBeforeCancelWhenDirty: true,
          onConfirm: async (newContact: Contact) => {
            const dealContact = mapContactToDealContact(newContact)
            const updatedDeal = {
              ...deal,
              contact: dealContact
            }
            setDeal(updatedDeal)
            setContact(newContact)
            await saveDeal({ deal: updatedDeal })
            dismissAllDialogs()
            enqueueSnackbar('Contact successfully linked', { variant: 'success' })
          },
          onCancel: dismissDialog
        })
      },
      onConfirm: async (newContact: Contact, _currentDealId?: number | string, _syncProvider?: string) => {
        const dealContact = mapContactToDealContact(newContact)
        const updatedDeal = {
          ...deal,
          contact: dealContact
        }
        setDeal(updatedDeal)
        setContact(newContact)
        await saveDeal({ deal: updatedDeal })
        enqueueSnackbar('Contact successfully linked', { variant: 'success' })
        dismissAllDialogs()
      },
      onCancel: dismissDialog
    })
  }

  function handleEditDealContact(showSwap?: boolean) {
    openDialog(ContactDialog, {
      contactId: deal?.contact?.id,
      disableDelete: true,
      confirmBeforeCancelWhenDirty: true,
      onConfirm: (contact: Contact) => {
        const { _rev, ...cleanContact } = contact
        const dealContact = mapContactToDealContact(contact)
        setDeal({
          ...deal,
          contact: {
            ...deal?.contact,
            ...dealContact
          }
        })
        if (cleanContact._id !== contact._id) {
          setContact(contact)
        }
        dismissAllDialogs()
      },
      onSwap: showSwap
        ? () => {
            handleNewContact()
          }
        : null,
      onCancel: dismissDialog
    })
  }

  async function handleArchiveDeal({ unarchive = false }: { unarchive?: boolean }) {
    track('deal_archived', { dealId: deal._id })
    try {
      await archiveDeals([deal._id], { unarchive })
      setDeal((prev) => ({
        ...prev,
        archived: !unarchive
      }))
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('Unable to archive Deal', { variant: 'error' })
    }
  }

  function handleDeleteDeal() {
    openDialog(ConfirmationDialog, {
      message: <span>This will permanently delete {deal.name}.</span>,
      onConfirm: async () => {
        track('deal_deleted', { dealId: deal._id })
        try {
          await deleteDeals([deal._id])
          setTimeout(() => {
            history.push('/deals/list', { fullLoad: true })
          }, 500)
        } catch (error) {
          Sentry.captureException(error, { tags: { dealId: deal?._id } })
          enqueueSnackbar('Unable to delete Deal', { variant: 'error' })
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handleEditDeal() {
    openDialog(DealDialog, {
      propsDeal: deal,
      onConfirm: (deal: Deal) => {
        setDeal(deal)
        handleSetViewTab('details')
        if (isMobile) {
          handleEventTabClick(DEAL_DETAILS_TAB)
        }
        setTimeout(() => {
          setRefreshToggle((prev) => !prev)
        }, 2000)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handleEditDealTags() {
    openDialog(EditTagsDialog, {
      appliedTags: deal.tags,
      onConfirm: async (tags: string[]) => {
        try {
          const savedDeal = await saveDeal({
            deal: {
              ...deal,
              tags
            }
          })
          setDeal(savedDeal.deal)
          debouncedActivityRefresh()
        } catch (error) {
          enqueueSnackbar('Unable to save Deal', { variant: 'error' })
          Sentry.captureException(error, { tags: { dealId: deal?._id } })
        }

        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handleEditProductionEvent(event: CalendarEvent) {
    openDialog(ProductionEventDialog, {
      isNew: false,
      event,
      onConfirm: async (updatedEvent: CalendarEvent) => {
        await saveEvent({
          variables: {
            event: updatedEvent
          },
          refetchQueries: []
        })
        setProductionEvent((prev) => ({
          ...prev,
          ...updatedEvent
        }))
        setTimeout(() => {
          setActivityRefreshToggle((prev) => !prev)
        }, 1000)
        dismissDialog()
      },
      onCancelEvent: (event: CalendarEvent) => {
        dismissDialog()
        handleCancelProductionEvent(event)
      }
    })
  }

  function handleCancelProductionEvent(event: CalendarEvent) {
    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message: <span>This will delete the production event.</span>,
      onConfirm: async () => {
        await saveEvent({
          variables: {
            event: {
              ...event,
              canceled: true
            }
          },
          refetchQueries: []
        })
        setProductionEvent(null)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  async function handleUpdateProductionEvent(event: CalendarEvent) {
    setProductionEvent((prev) => ({
      ...prev,
      ...event
    }))

    await saveEvent({
      variables: {
        event
      },
      refetchQueries: []
    })

    setTimeout(() => {
      setActivityRefreshToggle((prev) => !prev)
    }, 1000)
    return true
  }

  function handleDeleteDealTag(tag: QuoteTag) {
    openDialog(ConfirmationDialog, {
      message: <span>This will remove the tag from the deal. You can add it back later if needed.</span>,
      onConfirm: async () => {
        try {
          const savedDeal = await saveDeal({
            deal: {
              ...deal,
              tags: deal.tags.filter((t) => t !== tag?.name)
            }
          })
          setDeal(savedDeal.deal)
        } catch (error) {
          enqueueSnackbar('Unable to save Deal', { variant: 'error' })
          Sentry.captureException(error, { tags: { dealId: deal?._id } })
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  async function handleStageClick(updatedDeal: Deal) {
    track('deal_stage_changed', { dealId: updatedDeal._id, toStage: updatedDeal.stage.value })
    const res = await updateDealStage({
      variables: {
        dealId: updatedDeal._id,
        stage: updatedDeal.stage.value
      }
    })
    setDeal(res.data.updateDealStage.deal)
    debouncedActivityRefresh()
  }

  async function handleAddTaskTemplate(id: string) {
    const taskTemplates = getTaskTemplateOptions({ options })
    const taskTemplate = taskTemplates.find((template) => template.key === id)
    if (!taskTemplate) return

    const tasks = createTasksFromTemplate({
      template: taskTemplate,
      deal,
      overrides: {
        owner: {},
        startDateTime: undefined
      }
    })

    setAddingTemplate({ key: taskTemplate.key, label: taskTemplate.label })

    // skip activity on saving task other than the first one
    const savedResults = await Promise.all(
      tasks.map((task, i) => saveTask({ task: task as Task, skipActivity: i !== 0 }))
    )
    setNewlyCreatedTasks(savedResults.map((r) => r.task))
    setAddingTemplate(null)
  }

  const activeActivityTab = activitiesTab.find((tab) => tab.active)

  function getActivityTabContent(activeTab) {
    switch (activeTab?.value) {
      case 'all':
        return (
          <NotesDocList
            key={String(activityRefreshToggle)}
            docId={deal?._id}
            disabled={!canEdit}
            query={activityQuery}
            onSaveNote={handleCreateNote}
            onDeleteNote={handleDeleteNote}
            onEditNote={handleEditNote}
            onEditNoteFiles={handleEditNoteFile}
            onNoteFileDetails={handleNoteFileDetails}
            noResultsMessage="No activity found."
          />
        )
      case 'chat':
        return (
          <DashboardActivity
            query={chatQuery}
            onUpdate={() => {}}
            noResultsMessage="No messages found."
            showContent={true}
            ActivityComponent={ChatActivityComponent}
          />
        )
      case 'notes':
        return (
          <NotesDocList
            docId={deal?._id}
            query={notesQuery}
            disabled={!canEdit}
            onSaveNote={handleCreateNote}
            onDeleteNote={handleDeleteNote}
            onEditNote={handleEditNote}
            onEditNoteFiles={handleEditNoteFile}
            onNoteFileDetails={handleNoteFileDetails}
          />
        )
      case 'files':
        return (
          <FilesDocList
            disabled={!canEdit}
            docId={deal?._id}
            files={dealFiles}
            onUpload={(files) => handleFilesChange({ files })}
          />
        )
      case 'tasks':
        return (
          <DealTaskDocList
            limit={5}
            onNewClick={handleNewItemClick}
            dealId={deal?._id}
            taskTemplateOrder={deal?.taskTemplateOrder || []}
            addingTemplate={addingTemplate}
            newTasks={newlyCreatedTasks}
            onReorderTaskTemplates={async (newOrder) => {
              const updatedDeal = { ...deal, taskTemplateOrder: newOrder }
              setDeal(updatedDeal)
              await saveDeal({ deal: updatedDeal })
            }}
          />
        )
      default:
        return null
    }
  }

  const MemoizedMapComponent = useCallback(React.memo(MapContainer), [deal?.jobAddress])

  function getEventTabContent(activeEventTab) {
    switch (activeEventTab?.value) {
      case DEAL_DETAILS_TAB:
        return <DetailDocList details={details} />
      case CONTACT_DETAILS_TAB:
        return <DetailDocList details={contactDetails} />
      case 'events':
      case 'productionEvent':
        return isProductionDeal ? (
          <MemoizedProductionEventDoc
            event={productionEvent}
            hideTitle
            hideNew
            loading={productionEventLoading}
            handleEdit={handleEditProductionEvent}
            handleCancel={handleCancelProductionEvent}
            handleUpdate={handleUpdateProductionEvent}
            handleCreate={() => handleNewItemClick({ type: 'productionEvent' })}
          />
        ) : (
          <EventDocList
            onEventsChange={() => debouncedActivityRefresh()}
            hideSinglePagePagination
            query={jsonToLucene({
              filters: [
                {
                  id: DETAILS_ACTIVITY_TYPE,
                  type: 'equal',
                  value: 'event'
                },
                { id: DETAILS_DEAL_ID, type: 'equal', value: id },
                { id: 'trashed', type: 'equal', not: true, value: true },
                { id: 'details.production', type: 'equal', value: true, not: true }
              ]
            })}
          />
        )
      case 'quotes':
        return (
          <QuoteDocList
            isLoading={isLoading}
            quotes={relatedQuotes}
            onRemove={handleRemoveQuoteLink}
            onDelete={(ids) => handleRemoveQuoteLink(ids[0], true)}
            query={getQuoteListQuery()}
            noResultsMessage={`No ${objectLabels.quote.value.toLowerCase()} found.`}
          />
        )
      case 'invoices':
        return (
          <QuoteDocList
            isLoading={isLoading}
            quotes={relatedInvoices}
            hideActions
            isInvoice
            query={getInvoiceListQuery()}
            noResultsMessage={`No invoice found.`}
          />
        )
      case 'job-costing':
        return (
          <JobCostingCard
            cardDisplay
            deal={deal}
            onSave={async (updatedDeal) => {
              setDeal(updatedDeal)
              try {
                const { deal: savedDeal } = await saveDeal({ deal: updatedDeal })
                if (savedDeal) setDeal(savedDeal)
              } catch (e) {
                setDeal(deal)
                console.log({ e })
                enqueueSnackbar('Unable to save job costing', { variant: 'error' })
              }
            }}
          />
        )
      case 'payments':
        return <DealPaymentsTable relatedDocs={relatedInvoices} hideTitle onPaymentConfirm={debouncedActivityRefresh} />
      case 'tasks':
        return (
          <DealTaskDocList
            limit={5}
            onNewClick={handleNewItemClick}
            dealId={deal?._id}
            taskTemplateOrder={deal?.taskTemplateOrder || []}
            addingTemplate={addingTemplate}
            onTasksChange={() => debouncedActivityRefresh()}
            newTasks={newlyCreatedTasks}
            onReorderTaskTemplates={async (newOrder) => {
              const updatedDeal = { ...deal, taskTemplateOrder: newOrder }
              setDeal(updatedDeal)
              await saveDeal({ deal: updatedDeal })
            }}
          />
        )
      case WORK_ORDER_TAB:
        return <DealWorkOrderCard relatedDoc={relatedInvoices[0]} loading={loading} hideTitle />
      default:
        return null
    }
  }

  const EventDocComponent = () => {
    return (
      <Paper>
        <EventDocList
          classes={{ root: 'tour-deal-events-card' }}
          handleCreate={canCreateNewItems ? () => handleNewItemClick({ type: 'event' }) : null}
          title={'Events'}
          onEventsChange={() => debouncedActivityRefresh()}
          hideSinglePagePagination
          query={jsonToLucene({
            filters: [
              {
                id: DETAILS_ACTIVITY_TYPE,
                type: 'equal',
                value: 'event'
              },
              { id: DETAILS_DEAL_ID, type: 'equal', value: id },
              { id: 'trashed', type: 'equal', not: true, value: true },
              { id: 'details.production', type: 'equal', value: true, not: true }
            ]
          })}
        />
      </Paper>
    )
  }

  const MemoizedEventDocComponent = useCallback(React.memo(EventDocComponent), [
    deal?._id,
    activityRefreshToggle,
    deal?.contact?.id
  ])

  // deal?.contact reference changes every time the server returns a fresh deal (e.g. after saving
  // JobCostingCard), which would remount this card that sits directly above JobCostingCard and
  // cause content below it to shift. Compare by the contact's stable id instead.
  const MemoizedProductionEventDoc = useCallback(React.memo(ProductionEventDoc), [
    deal?._id,
    activityRefreshToggle,
    deal?.contact?.id,
    productionEvent
  ])

  // const TaskDocComponent = () => {
  //   return (
  //     <Paper>
  //       <TaskDocList
  //         classes={{ root: 'tour-deal-tasks-card' }}
  //         title={'Tasks'}
  //         handleCreate={canCreateNewItems ? () => handleNewItemClick({ type: 'task' }) : null}
  //         dealId={deal?._id}
  //         dense
  //         onTasksChange={() =>
  //           setTimeout(() => {
  //             setActivityRefreshToggle((prev) => !prev)
  //           }, 2000)
  //         }
  //       />
  //     </Paper>
  //   )
  // }

  // const MemoizedTaskDocComponent = useCallback(React.memo(TaskDocComponent), [deal?._id, refreshToggle])

  const InvoiceDocComponent = () => {
    return (
      <QuoteDocList
        isLoading={isLoading}
        quotes={relatedInvoices}
        handleCreate={
          isLoading || !canEdit
            ? null
            : relatedQuotes.length > 0
              ? (multipleInvoicesFeature || relatedInvoices.length === 0) && !canCreateQuotes
                ? () => handleNewItemClick({ type: 'invoice' })
                : null
              : null
        }
        isInvoice
        hideActions
        query={getInvoiceListQuery()}
        noResultsMessage={`No invoice found.`}
      />
    )
  }

  // NOTE: use deal?._id (stable for the lifetime of the deal) rather than the full `deal` object.
  // Every setDeal (e.g. saving JobCostingCard) replaces the deal reference, which would regenerate
  // this useCallback, swap the React.memo wrapper for a new one, and cause React to unmount+mount
  // this list — producing a visible content shift on EditDeal while saving unrelated fields.
  const MemoizedInvoiceDocList = useCallback(React.memo(InvoiceDocComponent), [
    canCreateQuotes,
    deal?._id,
    relatedQuotes,
    refreshToggle,
    isLoading,
    relatedInvoices,
    relatedInvoices?.length
  ])

  const PaymentsListComponent = () => {
    return (
      <Paper>
        <DealPaymentsTable
          relatedDocs={relatedInvoices.filter(Boolean)}
          addClickHandler={handleAddPayment}
          onPaymentConfirm={debouncedActivityRefresh}
        />
      </Paper>
    )
  }

  const MemoizedPaymentsList = useCallback(React.memo(PaymentsListComponent), [relatedInvoices, refreshToggle])
  const QuoteDocComponent = () => {
    return (
      <QuoteDocList
        classes={{ root: 'tour-deal-quotes-card' }}
        isLoading={loading}
        quotes={relatedQuotes}
        hideActions
        handleCreate={
          relatedQuotes.length > 0 || loading || !canCreateQuotes || !canEdit
            ? null
            : () => handleNewItemClick({ type: 'quotes' })
        }
        onRemove={handleRemoveQuoteLink}
        onDelete={(ids) => handleRemoveQuoteLink(ids[0], true)}
        query={getQuoteListQuery()}
        noResultsMessage={`No ${objectLabels.quote.value.toLowerCase()} found.`}
      />
    )
  }

  // See note above on MemoizedInvoiceDocList — use deal?._id instead of the full deal object so
  // unrelated setDeal calls don't remount this list and shift the page layout.
  const MemoizedQuoteDocList = useCallback(React.memo(QuoteDocComponent), [
    loading,
    deal?._id,
    data?.deal?._id,
    canCreateQuotes,
    relatedQuotes,
    refreshToggle,
    relatedQuotes?.length
  ])

  if (error) {
    Sentry.captureException(error)
    enqueueSnackbar('Unable to load Deal', { variant: 'error' })
    return <Redirect to="/deals/board" />
  }

  if (!crmFlag || (isProductionDeal && !productionFlag)) {
    return <RedirectHome />
  }

  return (
    <BasePage width={'xl'}>
      <DocumentTitle title="Deal" />
      <motion.div key={loading.toString()} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {loading || !deal ? (
          <EditDealShim />
        ) : (
          <div className={classes.contentWrapper} style={{ pointerEvents: isTrashed ? 'none' : 'auto' }}>
            {isTrashed && (
              <div style={{ marginBottom: 16, position: 'relative', zIndex: 1001, pointerEvents: 'auto' }}>
                <RecoveryNotice
                  deal={deal}
                  itemLabel="deal"
                  onRestoreDeal={async () => {
                    const result = await untrashDeals([deal._id])
                    if (result?.data?.untrashDeals?.[0] && !result.data.untrashDeals[0].error) {
                      const updatedDeal: Deal = {
                        ...deal,
                        ...result.data.untrashDeals[0],
                        __typename: 'Deal'
                      }
                      setDeal(updatedDeal)
                      setTimeout(() => {
                        refetchRelatedQuotes()
                      }, 500)
                    }
                  }}
                />
              </div>
            )}
            <DealHeader
              production={isProductionDeal}
              canCreateEvent={isProductionDeal ? !productionEvent && canCreateNewItems : canCreateNewItems}
              handleNewClick={isTrashed ? null : handleNewItemClick}
              canCreateQuotes={isTrashed ? false : canAddQuotes || canCreateQuotes}
              canCreatePayment={
                relatedInvoices.length > 0 &&
                !relatedInvoices.every((doc) => doc.totals.balance_due === 0) &&
                canEdit &&
                !isTrashed
              }
              handleSaveName={async (name) => {
                const newDeal: Deal = {
                  ...deal,
                  name
                }
                setDeal(newDeal)
                await saveDeal({ deal: newDeal })
                debouncedActivityRefresh()
                return true
              }}
              canCreateInvoices={
                isTrashed
                  ? false
                  : permissionToCreateInvoice &&
                    relatedQuotes.length > 0 &&
                    (multipleInvoicesFeature || relatedInvoices.length === 0) &&
                    !isLoading
              }
              hideDisabledActions={!canCreateQuotes}
              warning={combinedWarning}
              roles={['admin', 'sales', 'viewer']}
              item={{
                ...deal,
                address: stringifyObjectValues({
                  obj: deal?.jobAddress,
                  fields: ['address', 'city', 'province', 'postal']
                }),
                city: deal?.jobAddress?.city,
                province: deal?.jobAddress?.province,
                country: deal?.jobAddress?.country,
                postal: deal?.jobAddress?.postal,
                phone_number: deal?.contact?.phoneNumber,
                email: deal?.contact?.email,
                preferred_contact_method: deal?.contact?.preferredContactMethod
              }}
              actions={
                canEdit || canDelete
                  ? [
                      ...(canEdit
                        ? [
                            {
                              key: 'edit',
                              label: 'Edit',
                              value: 'edit'
                            }
                          ]
                        : []),
                      ...(canDelete
                        ? [
                            {
                              key: 'delete',
                              label: 'Delete',
                              value: 'delete'
                            }
                          ]
                        : []),
                      ...(canEdit
                        ? [
                            deal.archived
                              ? {
                                  key: 'unarchive',
                                  label: 'Unarchive',
                                  value: 'unarchive'
                                }
                              : {
                                  key: 'archive',
                                  label: 'Archive',
                                  value: 'archive'
                                }
                          ]
                        : [])
                    ]
                  : []
              }
              handleAction={async (action: string) => {
                switch (action) {
                  case 'edit':
                    handleEditDeal()
                    break
                  case 'delete':
                    handleDeleteDeal()
                    break
                  case 'archive':
                    await handleArchiveDeal({})
                    break
                  case 'unarchive':
                    await handleArchiveDeal({ unarchive: true })
                    break
                }
              }}
              onOwnerChange={
                isTrashed
                  ? undefined
                  : async (estimatorOption: DashboardEstimator) => {
                      const newDeal = updateDealOwner({
                        deal,
                        newOwner: estimatorOption
                      })
                      setDeal(newDeal)
                      await saveDeal({ deal: newDeal })
                      debouncedActivityRefresh()
                    }
              }
            />
            <Hidden smDown>
              <PipelineProgressBar
                handleStageClick={canUpdateDealStage ? handleStageClick : null}
                deal={deal}
                pipeline={pipeline}
              />
              {deal.archived && (
                <Grid item xs={12} style={{ margin: '6px 0' }}>
                  <Alert severity={'warning'} variant={'default'} content={'This deal is archived.'} />
                </Grid>
              )}
            </Hidden>
            <div className={classes.body}>
              {/* XL */}
              <Hidden lgDown>
                <Grid container spacing={0}>
                  {/* Left */}
                  <Grid item xs={3} className={classes.gridItem} style={{ padding: 0, paddingRight: '14px' }}>
                    <DetailsContainer
                      classes={{ paper: 'tour-deal-details-card' }}
                      details={details}
                      actions={canEdit ? [{ key: 'edit', label: 'Edit', value: 'edit' }] : []}
                      handleAction={
                        canEdit
                          ? (action: string) => {
                              switch (action) {
                                case 'edit':
                                  handleEditDeal()
                                  break
                              }
                            }
                          : null
                      }
                    />
                    <MemoizedMapComponent item={deal.jobAddress} geocode={deal.geocode} />
                    <DetailsContainer
                      title={'Contact'}
                      actions={[
                        {
                          key: 'edit',
                          label: 'Edit',
                          value: 'edit'
                        },
                        {
                          key: 'swap',
                          label: 'Swap Contact',
                          value: 'swap'
                        }
                      ]}
                      handleAction={
                        canEdit
                          ? (action: string) => {
                              switch (action) {
                                case 'edit':
                                  handleEditDealContact()
                                  break
                                case 'swap':
                                  handleNewContact()
                                  break
                              }
                            }
                          : null
                      }
                      details={contactDetails}
                      handleLink={() => {
                        goToContact(contact._id)
                      }}
                    />
                    {/* {companyDetails.length > 0 && (
                      <DetailsContainer
                        title={'Company'}
                        details={companyDetails}
                        handleEdit={handleEditDealCompany}
                        handleLink={() => {
                          goToCompany(company._id)
                        }}
                      />
                    )} */}
                  </Grid>
                  {/* Mid */}
                  <Grid
                    item
                    xs={6}
                    className={classNames(classes.gridItem, 'tour-deal-middle-container')}
                    style={{ padding: 0, paddingRight: '14px' }}
                  >
                    {productionFlag && isProductionDeal && (
                      <DealWorkOrderCard relatedDoc={relatedInvoices[0]} loading={loading} />
                    )}
                    <DocsContainer handleTabClick={handleActivityTabClick} tabs={activitiesTab}>
                      {getActivityTabContent(activeActivityTab)}
                    </DocsContainer>
                  </Grid>
                  {/* Right */}
                  <Grid item xs={3} className={classes.gridItem} style={{ padding: 0 }}>
                    {isProductionDeal ? (
                      <>
                        <MemoizedProductionEventDoc
                          event={productionEvent}
                          loading={productionEventLoading}
                          handleEdit={handleEditProductionEvent}
                          handleCancel={handleCancelProductionEvent}
                          handleUpdate={handleUpdateProductionEvent}
                          handleCreate={() => handleNewItemClick({ type: 'productionEvent' })}
                        />
                        <JobCostingCard
                          deal={deal}
                          onSave={async (updatedDeal) => {
                            setDeal(updatedDeal)
                            try {
                              console.log({ updatedDeal })
                              const { deal: savedDeal } = await saveDeal({ deal: updatedDeal })
                              if (savedDeal) setDeal(savedDeal)
                            } catch (e) {
                              console.log({ e })
                              setDeal(deal)
                              enqueueSnackbar('Unable to save job costing', { variant: 'error' })
                            }
                          }}
                        />
                      </>
                    ) : (
                      <MemoizedEventDocComponent />
                    )}
                    {/* <MemoizedTaskDocComponent /> */}
                    <QuoteDocCard
                      onRemove={handleRemoveQuoteLink}
                      onDelete={(ids) => handleRemoveQuoteLink(ids[0], true)}
                      disabled={trialExpired}
                      label={objectLabels.quote.value}
                      hasInvoice={relatedInvoices.length > 0}
                      loading={isLoading}
                      quote={
                        relatedQuotes[0]
                          ? mapQuoteToRow({
                              quote: relatedQuotes[0],
                              options,
                              user,
                              quoteTypes: quoteTypeOptions
                            })
                          : null
                      }
                      estimatesTab={estimatesTab}
                      onChangeTab={(tab) => setEstimatesTab(tab)}
                      onCreate={() =>
                        handleNewItemClick({ type: estimatesTab === TAB_ESTIMATES ? 'quotes' : 'invoice' })
                      }
                    >
                      {estimatesTab === TAB_ESTIMATES ? <MemoizedQuoteDocList /> : <MemoizedInvoiceDocList />}
                    </QuoteDocCard>
                    <MemoizedPaymentsList />
                    <TagContainer
                      disabled={!canEdit}
                      tags={dealTags}
                      handleEdit={handleEditDealTags}
                      handleDelete={handleDeleteDealTag}
                    />
                  </Grid>
                </Grid>
              </Hidden>
              {/* Large */}
              <Hidden smDown xlUp>
                <Grid container spacing={2}>
                  {/* Left */}
                  <Grid item xs={6} className={classes.gridItem} style={{ padding: 0, paddingRight: '8px' }}>
                    <DetailsContainer
                      details={details}
                      contactDetails={contactDetails}
                      handleEdit={
                        canEdit
                          ? (type) => {
                              if (type === 'deal') {
                                handleEditDeal()
                              } else {
                                handleEditDealContact(true)
                              }
                            }
                          : null
                      }
                    />
                    <DocsContainer
                      handleTabClick={handleEventTabClick}
                      handleNewClick={canCreateNewItems ? handleNewItemClick : null}
                      tabs={eventsTab}
                    >
                      {getEventTabContent(activeEventTab)}
                    </DocsContainer>
                    {isProductionDeal && <MemoizedMapComponent item={deal.jobAddress} geocode={deal.geocode} />}
                    <TagContainer
                      disabled={!canEdit}
                      tags={dealTags}
                      handleEdit={handleEditDealTags}
                      handleDelete={handleDeleteDealTag}
                    />
                  </Grid>
                  {/* Right */}
                  <Grid item xs={6} className={classes.gridItem} style={{ padding: 0, paddingLeft: '8px' }}>
                    {productionFlag && isProductionDeal && (
                      <>
                        <DealWorkOrderCard relatedDoc={relatedInvoices[0]} loading={loading} />
                        <MemoizedProductionEventDoc
                          event={productionEvent}
                          loading={productionEventLoading}
                          handleEdit={handleEditProductionEvent}
                          handleCancel={handleCancelProductionEvent}
                          handleUpdate={handleUpdateProductionEvent}
                          handleCreate={() => handleNewItemClick({ type: 'productionEvent' })}
                        />
                      </>
                    )}
                    {!isProductionDeal && <MemoizedMapComponent item={deal.jobAddress} geocode={deal.geocode} />}

                    <DocsContainer handleTabClick={handleActivityTabClick} tabs={activitiesTab}>
                      {getActivityTabContent(activeActivityTab)}
                    </DocsContainer>
                  </Grid>
                </Grid>
              </Hidden>
              {/* Small */}
              <Hidden mdUp>
                <Grid item xs={12}>
                  <div className={classes.tabs} style={productionFlag && isMobile ? { marginBottom: '16px' } : {}}>
                    <Tabs
                      value={viewTab}
                      onChange={(ev, newTab) => {
                        handleSetViewTab(newTab)
                      }}
                    >
                      <Tab value="details" label="Overview" />
                      <Tab disabled label="|" />
                      <Tab value="activities" label="Timeline" />
                    </Tabs>
                  </div>
                  <Hidden smDown={productionFlag}>
                    <PipelineProgressBar
                      handleStageClick={canEdit ? handleStageClick : null}
                      deal={deal}
                      pipeline={pipeline}
                    />
                  </Hidden>

                  {productionFlag && viewTab === 'details' && (
                    <Grid item xs={12} style={{ marginBottom: 16 }}>
                      <PipelineSelectContainer handleStageClick={handleStageClick} deal={deal} pipeline={pipeline} />
                    </Grid>
                  )}

                  {viewTab === 'details' ? (
                    <>
                      <DocsContainer
                        handleTabClick={handleEventTabClick}
                        handleNewClick={canEdit ? handleNewItemClick : null}
                        tabs={eventsTab}
                      >
                        {getEventTabContent(activeEventTab)}
                      </DocsContainer>
                      <br />
                      {activeEventTab?.value === DEAL_DETAILS_TAB && (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <MemoizedMapComponent item={deal.jobAddress} geocode={deal.geocode} />
                          </Grid>
                          <Grid item xs={12}>
                            <TagContainer
                              disabled={!canEdit}
                              tags={dealTags}
                              handleEdit={handleEditDealTags}
                              handleDelete={handleDeleteDealTag}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <DocsContainer handleTabClick={handleActivityTabClick} tabs={activitiesTab}>
                      {getActivityTabContent(activeActivityTab)}
                    </DocsContainer>
                  )}
                </Grid>
              </Hidden>
            </div>
          </div>
        )}
      </motion.div>
    </BasePage>
  )
}

export default EditDeal
